import { config } from './config';
import type { OsmTagFilter } from './searchIntent';
import type { LatLng } from './types';
import { yieldToMain } from './mainThread';

/**
 * Destination POI lookup via Overpass (amenity / shop / tourism).
 * Uses the existing `/v2/overpass` proxy — not the along-route HUD amenities.
 */

export type AmenityPoi = {
  name: string;
  lat: number;
  lng: number;
  kind: string;
};

type OverpassElement = {
  type: 'node' | 'way' | 'relation';
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
};

type OverpassResponse = {
  elements?: OverpassElement[];
};

const DEFAULT_RADIUS_M = 30_000;
const DEFAULT_LIMIT = 24;
const TIMEOUT_S = 20;

function foldText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

function overpassUrl(): string {
  if (config.mode === 'proxy') return `${config.proxyUrl}/v2/overpass`;
  return '/v2/overpass';
}

function escapeOverpassRegex(value: string): string {
  return value.replace(/[\\^$.|?*+()[\]{}]/g, '\\$&');
}

function buildQuery(filters: OsmTagFilter[], focus: LatLng, radiusM: number): string {
  const parts: string[] = [];
  for (const f of filters) {
    const tag = `["${f.key}"="${f.value}"]`;
    parts.push(`node${tag}(around:${radiusM},${focus.lat},${focus.lng});`);
    parts.push(`way${tag}(around:${radiusM},${focus.lat},${focus.lng});`);
  }
  return `[out:json][timeout:${TIMEOUT_S}];(${parts.join('')});out center ${DEFAULT_LIMIT};`;
}

/** Brand / free-text POI: match name, brand, or operator near focus. */
function buildNamedQuery(nameHint: string, focus: LatLng, radiusM: number): string {
  const re = escapeOverpassRegex(nameHint.trim());
  const around = `(around:${radiusM},${focus.lat},${focus.lng})`;
  const keys = ['name', 'brand', 'operator'] as const;
  const poiKeys = ['shop', 'amenity', 'tourism', 'healthcare', 'leisure', 'office'] as const;
  const parts: string[] = [];
  for (const key of keys) {
    for (const poi of poiKeys) {
      const tag = `["${key}"~"${re}",i]["${poi}"]`;
      parts.push(`node${tag}${around};`);
      parts.push(`way${tag}${around};`);
    }
  }
  return `[out:json][timeout:${TIMEOUT_S}];(${parts.join('')});out center ${DEFAULT_LIMIT};`;
}

function elementName(tags: Record<string, string> | undefined, kind: string): string {
  const name = tags?.name?.trim() || tags?.brand?.trim() || tags?.operator?.trim();
  if (name) return name;
  return kind;
}

function elementKind(tags: Record<string, string> | undefined): string {
  if (!tags) return 'poi';
  return (
    tags.amenity?.trim() ||
    tags.shop?.trim() ||
    tags.tourism?.trim() ||
    tags.healthcare?.trim() ||
    'poi'
  );
}

function matchesNameHint(name: string, hint: string): boolean {
  if (!hint) return true;
  const hay = foldText(name);
  const needle = foldText(hint);
  if (needle.length < 2) return true;
  return hay.includes(needle);
}

async function fetchOverpassPois(
  query: string,
  opts: {
    limit: number;
    nameHint: string;
    signal?: AbortSignal;
  },
): Promise<AmenityPoi[]> {
  try {
    const res = await fetch(overpassUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(query),
      signal: opts.signal,
    });
    if (!res.ok) return [];
    const raw = await res.text();
    await yieldToMain();
    const json = JSON.parse(raw) as OverpassResponse;
    const elements = json.elements ?? [];
    const seen = new Set<string>();
    const out: AmenityPoi[] = [];
    const nameHint = opts.nameHint;

    for (const el of elements) {
      const lat = el.type === 'node' ? el.lat : el.center?.lat;
      const lng = el.type === 'node' ? el.lon : el.center?.lon;
      if (lat === undefined || lng === undefined) continue;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const kind = elementKind(el.tags);
      const name = elementName(el.tags, kind);
      if (nameHint) {
        const brand = el.tags?.brand?.trim() ?? '';
        const op = el.tags?.operator?.trim() ?? '';
        if (!matchesNameHint(`${name} ${brand} ${op}`, nameHint)) continue;
      }
      const key = `${lat.toFixed(4)},${lng.toFixed(4)},${foldText(name)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ name, lat, lng, kind });
      if (out.length >= opts.limit) break;
    }
    return out;
  } catch (err) {
    if ((err as Error).name === 'AbortError') return [];
    return [];
  }
}

/**
 * Nearby OSM amenities for a detected search intent.
 * Failures return [] so ORS/Photon can still answer.
 */
export async function searchAmenityPois(
  filters: OsmTagFilter[],
  focus: LatLng,
  opts: {
    radiusM?: number;
    limit?: number;
    nameHint?: string;
    signal?: AbortSignal;
  } = {},
): Promise<AmenityPoi[]> {
  if (filters.length === 0) return [];
  const radiusM = opts.radiusM ?? DEFAULT_RADIUS_M;
  const limit = opts.limit ?? DEFAULT_LIMIT;
  const nameHint = opts.nameHint?.trim() ?? '';
  return fetchOverpassPois(buildQuery(filters, focus, radiusM), {
    limit,
    nameHint,
    signal: opts.signal,
  });
}

/**
 * Nearby OSM POIs by name / brand / operator (no amenity keyword required).
 * Failures return [] so ORS/Photon can still answer.
 */
export async function searchNamedPois(
  nameHint: string,
  focus: LatLng,
  opts: {
    radiusM?: number;
    limit?: number;
    signal?: AbortSignal;
  } = {},
): Promise<AmenityPoi[]> {
  const hint = nameHint.trim();
  if (hint.length < 2) return [];
  const radiusM = opts.radiusM ?? DEFAULT_RADIUS_M;
  const limit = opts.limit ?? DEFAULT_LIMIT;
  return fetchOverpassPois(buildNamedQuery(hint, focus, radiusM), {
    limit,
    nameHint: hint,
    signal: opts.signal,
  });
}
